import React, { useState, useEffect } from 'react';
import { Settings, Plus, X, Check, Trash2 } from 'lucide-react';
import * as agentService from '../../../../../services/agentService';
import { Agent, Tag } from '../../../../../types/agent';
import { getModelSelectOptions, getModelDisplayInfo } from '../../../../../config/models';
import { LoadingSpinner, MultiSelect } from '../../../../common';
import { AdminCard } from '../../../ui';
import { useWorkspace } from '../../../../../contexts/WorkspaceContext';
import { Workspace, WorkspaceGroup } from '../../../../../types/workspace';
import * as workspaceService from '../../../../../services/workspaceService';

interface AgentConfigurationProps {
  agent: Agent;
  onUpdate: () => void;
}

export const AgentConfiguration: React.FC<AgentConfigurationProps> = ({ agent, onUpdate }) => {
  const { currentWorkspace } = useWorkspace();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [groups, setGroups] = useState<(WorkspaceGroup & { workspace_name?: string })[]>([]);
  const [config, setConfig] = useState({
    name: agent.name,
    description: agent.description || '',
    retrieval_method: agent.retrieval_method || '',
    use_guided_prompt: agent.use_guided_prompt ?? true,
    use_security_prompt: agent.use_security_prompt ?? true,
    model: agent.model || 'claude-4-sonnet',
    temperature: agent.temperature ?? 0.7,
    prompt_sections: {
      identity: agent.prompt_sections?.identity || '',
      goal: agent.prompt_sections?.goal || '',
      response_style: agent.prompt_sections?.response_style || '',
      constraints: agent.prompt_sections?.constraints || '',
      custom_instructions: agent.prompt_sections?.custom_instructions || ''
    },
    custom_prompt: agent.custom_prompt || '',
    // Workspace multi-tenant fields
    visibility_level: agent.visibility_level || 'workspace',
    workspace_id: agent.created_by_workspace_id || null,
    group_id: agent.created_by_group_id || null,
    // Multi-workspace and multi-group visibility
    workspace_ids: agent.visible_workspace_ids || [],
    group_ids: agent.visible_group_ids || []
  });

  // Tag Management State
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [agentTags, setAgentTags] = useState<Tag[]>(agent.tags || []);
  const [newTagName, setNewTagName] = useState('');
  const [tagError, setTagError] = useState<string | null>(null);
  const [isTagLoading, setIsTagLoading] = useState(false);

  useEffect(() => {
    fetchAllTags();
    fetchWorkspaces();
  }, []);

  useEffect(() => {
    setAgentTags(agent.tags || []);
  }, [agent]);

  // Fetch groups from ALL selected workspaces
  useEffect(() => {
    const fetchGroupsFromAllWorkspaces = async () => {
      const selectedWorkspaceIds = config.workspace_ids;
      if (!selectedWorkspaceIds || selectedWorkspaceIds.length === 0) {
        setGroups([]);
        return;
      }

      try {
        const groupsPromises = selectedWorkspaceIds.map(async (wsId) => {
          const response = await workspaceService.getWorkspaceGroups(wsId);
          if (response.success && response.data) {
            const workspace = workspaces.find(w => w.id === wsId);
            return response.data.map(group => ({
              ...group,
              workspace_name: workspace?.name || `Workspace ${wsId}`
            }));
          }
          return [];
        });

        const allGroupsArrays = await Promise.all(groupsPromises);
        const allGroups = allGroupsArrays.flat();
        setGroups(allGroups);
      } catch (err) {
        console.error('Failed to fetch groups from workspaces:', err);
        setGroups([]);
      }
    };

    fetchGroupsFromAllWorkspaces();
  }, [config.workspace_ids, workspaces]);

  const fetchWorkspaces = async () => {
    try {
      const response = await workspaceService.getUserWorkspaces();
      if (response.success && response.data) {
        setWorkspaces(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch workspaces:', err);
    }
  };

  const fetchAllTags = async () => {
    setIsTagLoading(true);
    try {
      const tags = await agentService.getAllTags();
      setAllTags(tags);
    } catch (err) {
      setTagError(err instanceof Error ? err.message : 'Falha ao buscar tags');
    } finally {
      setIsTagLoading(false);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      setTagError('O nome da tag não pode estar vazio');
      return;
    }

    setIsTagLoading(true);
    setTagError(null);

    try {
      await agentService.createTag({ name: newTagName.trim() });
      setNewTagName('');
      await fetchAllTags();
    } catch (err) {
      setTagError(err instanceof Error ? err.message : 'Falha ao criar tag');
    } finally {
      setIsTagLoading(false);
    }
  };

  const handleDeleteTag = async (tagId: number) => {
    if (!window.confirm('Tem certeza que deseja excluir esta tag? Isso a removerá de todos os agentes.')) {
      return;
    }

    setIsTagLoading(true);
    setTagError(null);

    try {
      await agentService.deleteTag(tagId);
      await fetchAllTags();
      setAgentTags(prevTags => prevTags.filter(tag => tag.id !== tagId));
    } catch (err) {
      setTagError(err instanceof Error ? err.message : 'Falha ao excluir tag');
    } finally {
      setIsTagLoading(false);
    }
  };

  const handleAddTagToAgent = async (tagId: number) => {
    if (agentTags.some(tag => tag.id === tagId)) {
      return;
    }

    setIsTagLoading(true);
    setTagError(null);

    try {
      const updatedAgent = await agentService.addTagToAgent(agent.id, tagId);
      setAgentTags(updatedAgent.tags);
      onUpdate();
    } catch (err) {
      setTagError(err instanceof Error ? err.message : 'Falha ao adicionar tag ao agente');
    } finally {
      setIsTagLoading(false);
    }
  };

  const handleRemoveTagFromAgent = async (tagId: number) => {
    setIsTagLoading(true);
    setTagError(null);

    try {
      const updatedAgent = await agentService.removeTagFromAgent(agent.id, tagId);
      setAgentTags(updatedAgent.tags);
      onUpdate();
    } catch (err) {
      setTagError(err instanceof Error ? err.message : 'Falha ao remover tag do agente');
    } finally {
      setIsTagLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateTag();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (!config.use_guided_prompt && !config.custom_prompt?.trim()) {
        setError('O campo "Prompt de Sistema Personalizado" é obrigatório quando o construtor guiado está desativado.');
        setIsLoading(false);
        return;
      }

      const updateData = {
        name: config.name,
        description: config.description,
        // retrieval_method is intentionally omitted - it cannot be changed after creation
        use_guided_prompt: config.use_guided_prompt,
        use_security_prompt: config.use_security_prompt,
        model: config.model,
        temperature: config.temperature,
        prompt_sections: config.prompt_sections,
        custom_prompt: config.use_guided_prompt ? undefined : config.custom_prompt,
        // Workspace multi-tenant fields
        visibility_level: config.visibility_level,
        workspace_id: config.visibility_level === 'global' ? null : config.workspace_id,
        group_id: config.visibility_level !== 'group' ? null : config.group_id,
        // Multi-workspace and multi-group visibility
        workspace_ids: (config.visibility_level === 'workspace' || config.visibility_level === 'group') ? config.workspace_ids : [],
        group_ids: config.visibility_level === 'group' ? config.group_ids : []
      };

      const response = await agentService.updateAgent(agent.id, updateData);
      if (response.success) {
        setIsEditing(false);
        onUpdate();
      } else {
        setError(response.message || 'Falha ao atualizar configuração do agente');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao atualizar configuração do agente');
    } finally {
      setIsLoading(false);
    }
  };

  const inputBaseClass = `
    w-full px-4 py-3
    bg-[#1a1a1a] border border-[#333333]
    text-white placeholder-[#888888]
    rounded-lg
    focus:outline-none focus:border-[#F4CC04]
    disabled:opacity-60 disabled:cursor-not-allowed
  `;

  return (
    <div className="space-y-6">
      {/* Agent Configuration Card */}
      <AdminCard
        title="Configuração do Agente"
        headerAction={
          !isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="
                flex items-center gap-2 px-3 py-2
                bg-[#F4CC04] hover:bg-[#D4B104]
                text-black text-sm font-medium
                rounded-lg transition-colors
              "
            >
              <Settings className="w-4 h-4" />
              Editar
            </button>
          ) : null
        }
      >
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-white font-medium text-base border-b border-[#333333] pb-2">
              Informações Básicas
            </h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#cccccc] mb-2">
                  Nome
                </label>
                <input
                  type="text"
                  value={config.name}
                  onChange={(e) => setConfig(prev => ({ ...prev, name: e.target.value }))}
                  disabled={!isEditing}
                  required
                  className={inputBaseClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#cccccc] mb-2">
                  Descrição
                </label>
                <textarea
                  value={config.description}
                  onChange={(e) => setConfig(prev => ({ ...prev, description: e.target.value }))}
                  disabled={!isEditing}
                  rows={3}
                  className={`${inputBaseClass} resize-none`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#cccccc] mb-2">
                  Tipo de Recuperação
                </label>
                <select
                  value={config.retrieval_method}
                  onChange={(e) => setConfig(prev => ({ ...prev, retrieval_method: e.target.value }))}
                  disabled={true}
                  className={`${inputBaseClass} cursor-not-allowed opacity-60`}
                >
                  <option value="">Selecione o tipo de recuperação</option>
                  <option value="direct_context">Contexto Direto</option>
                  <option value="relational">Relacional</option>
                  <option value="semantic_vector">Semântico Vetorial</option>
                </select>
                <p className="text-xs text-[#888888] mt-2">
                  ⚠️ O tipo de recuperação não pode ser alterado após a criação do agente.
                </p>
              </div>
            </div>
          </div>

          {/* Model Configuration */}
          <div className="space-y-4">
            <h3 className="text-white font-medium text-base border-b border-[#333333] pb-2">
              Configurações do Modelo
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#cccccc] mb-2">
                  Modelo
                </label>
                <select
                  value={config.model}
                  onChange={(e) => setConfig(prev => ({ ...prev, model: e.target.value }))}
                  disabled={!isEditing}
                  className={`${inputBaseClass} cursor-pointer`}
                >
                  {getModelSelectOptions().map(option => {
                    const modelInfo = getModelDisplayInfo(option.value);
                    return (
                      <option key={option.value} value={option.value}>
                        {option.label}
                        {option.isNew && ' 🆕'}
                        {option.isRecommended && ' ⭐'}
                        {modelInfo && ` (${modelInfo.providerInfo.name})`}
                      </option>
                    );
                  })}
                </select>
                {config.model && (() => {
                  const modelInfo = getModelDisplayInfo(config.model);
                  return modelInfo ? (
                    <p className="text-xs text-[#888888] mt-2">
                      {modelInfo.description}
                    </p>
                  ) : null;
                })()}
              </div>
              <div>
                <label className="block text-sm font-medium text-[#cccccc] mb-2">
                  Temperature ({config.temperature})
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={config.temperature}
                  onChange={(e) => setConfig(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                  disabled={!isEditing}
                  className="
                    w-full h-2 bg-[#333333] rounded-lg appearance-none cursor-pointer
                    disabled:opacity-60 disabled:cursor-not-allowed
                    [&::-webkit-slider-thumb]:appearance-none
                    [&::-webkit-slider-thumb]:w-4
                    [&::-webkit-slider-thumb]:h-4
                    [&::-webkit-slider-thumb]:rounded-full
                    [&::-webkit-slider-thumb]:bg-[#F4CC04]
                    [&::-webkit-slider-thumb]:cursor-pointer
                  "
                />
                <div className="flex justify-between text-xs text-[#888888] mt-1">
                  <span>Preciso</span>
                  <span>Criativo</span>
                </div>
              </div>
            </div>
          </div>

          {/* Visibility Configuration */}
          <div className="space-y-4 border-t border-[#333333] pt-6">
            <h3 className="text-white font-medium text-base border-b border-[#333333] pb-2">
              Configurações de Visibilidade
            </h3>

            {/* Visibility Level */}
            <div>
              <label className="block text-sm font-medium text-[#cccccc] mb-2">
                Nível de Visibilidade
              </label>
              <select
                value={config.visibility_level || 'workspace'}
                onChange={(e) => {
                  const visibility_level = e.target.value as 'global' | 'workspace' | 'group';

                  // SECURITY: Non-TD users cannot select 'global'
                  if (!currentWorkspace?.is_td_workspace && visibility_level === 'global') {
                    return;
                  }

                  setConfig(prev => ({
                    ...prev,
                    visibility_level,
                    workspace_id: visibility_level === 'global' ? null : (prev.workspace_id || currentWorkspace?.id || null),
                    workspace_ids: (visibility_level === 'workspace' || visibility_level === 'group') ? prev.workspace_ids : [],
                    group_id: visibility_level !== 'group' ? null : prev.group_id,
                    group_ids: visibility_level === 'group' ? prev.group_ids : []
                  }));
                }}
                disabled={!isEditing}
                className={inputBaseClass}
              >
                {/* Only show "Global" option for TD workspace users */}
                {currentWorkspace?.is_td_workspace && (
                  <option value="global">Global - Todos os usuários</option>
                )}
                <option value="workspace">Workspace - Usuários do workspace</option>
                <option value="group">Grupo (Compartilhamento granular por workspaces e grupos)</option>
              </select>
              <p className="text-xs text-[#888888] mt-1">
                {config.visibility_level === 'global' && 'Agente visível para todos os usuários da plataforma'}
                {config.visibility_level === 'workspace' && 'Agente visível para usuários dos workspaces selecionados'}
                {config.visibility_level === 'group' && 'Selecione primeiro os workspaces, depois os grupos específicos que poderão ver este agente'}
              </p>
            </div>

            {/* Workspace Multi-Selection */}
            {(config.visibility_level === 'workspace' || config.visibility_level === 'group') && (
              <MultiSelect
                label="Workspaces"
                options={workspaces.map(w => ({ id: w.id, name: w.name }))}
                selectedIds={config.workspace_ids}
                onChange={(ids) => setConfig(prev => ({ ...prev, workspace_ids: ids }))}
                placeholder="Selecione os workspaces..."
                disabled={!isEditing}
                required
              />
            )}

            {/* Group Multi-Selection */}
            {config.visibility_level === 'group' && (
              <MultiSelect
                label="Grupos"
                options={groups
                  .filter((g): g is WorkspaceGroup & { workspace_name?: string } => !g.deleted)
                  .map(g => ({
                    id: g.id,
                    name: g.workspace_name ? `${g.name} - ${g.workspace_name}` : g.name
                  }))
                }
                selectedIds={config.group_ids}
                onChange={(ids) => setConfig(prev => ({ ...prev, group_ids: ids }))}
                placeholder="Selecione os grupos..."
                disabled={!isEditing}
                required
              />
            )}
          </div>

          {/* Prompt Configuration */}
          <div className="space-y-4">
            <h3 className="text-white font-medium text-base border-b border-[#333333] pb-2">
              Configuração do Prompt
            </h3>

            {/* Checkboxes */}
            <div className="flex flex-col sm:flex-row gap-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.use_guided_prompt}
                  onChange={(e) => setConfig(prev => ({ ...prev, use_guided_prompt: e.target.checked }))}
                  disabled={!isEditing}
                  className="
                    w-5 h-5 rounded border-2 border-[#333333] bg-[#1a1a1a]
                    checked:bg-[#F4CC04] checked:border-[#F4CC04]
                    focus:ring-2 focus:ring-[#F4CC04]/50
                    disabled:opacity-60 disabled:cursor-not-allowed
                  "
                />
                <span className="text-sm text-[#cccccc]">Usar Construtor de Prompt Guiado</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.use_security_prompt}
                  onChange={(e) => setConfig(prev => ({ ...prev, use_security_prompt: e.target.checked }))}
                  disabled={!isEditing}
                  className="
                    w-5 h-5 rounded border-2 border-[#333333] bg-[#1a1a1a]
                    checked:bg-[#F4CC04] checked:border-[#F4CC04]
                    focus:ring-2 focus:ring-[#F4CC04]/50
                    disabled:opacity-60 disabled:cursor-not-allowed
                  "
                />
                <span className="text-sm text-[#cccccc]">Incluir Diretrizes de Segurança</span>
              </label>
            </div>

            {/* Prompt Sections or Custom Prompt */}
            {config.use_guided_prompt ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#cccccc] mb-2">
                    Identidade do Agente
                  </label>
                  <textarea
                    value={config.prompt_sections.identity}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      prompt_sections: { ...prev.prompt_sections, identity: e.target.value }
                    }))}
                    disabled={!isEditing}
                    placeholder="Defina quem é seu agente e sua personalidade..."
                    rows={4}
                    className={`${inputBaseClass} resize-none`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#cccccc] mb-2">
                    Objetivo
                  </label>
                  <textarea
                    value={config.prompt_sections.goal}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      prompt_sections: { ...prev.prompt_sections, goal: e.target.value }
                    }))}
                    disabled={!isEditing}
                    placeholder="Defina o objetivo principal do seu agente..."
                    rows={4}
                    className={`${inputBaseClass} resize-none`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#cccccc] mb-2">
                    Estilo de Resposta
                  </label>
                  <textarea
                    value={config.prompt_sections.response_style}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      prompt_sections: { ...prev.prompt_sections, response_style: e.target.value }
                    }))}
                    disabled={!isEditing}
                    placeholder="Defina como seu agente deve estruturar suas respostas..."
                    rows={4}
                    className={`${inputBaseClass} resize-none`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#cccccc] mb-2">
                    Restrições
                  </label>
                  <textarea
                    value={config.prompt_sections.constraints}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      prompt_sections: { ...prev.prompt_sections, constraints: e.target.value }
                    }))}
                    disabled={!isEditing}
                    placeholder="Defina limitações e restrições para seu agente..."
                    rows={4}
                    className={`${inputBaseClass} resize-none`}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#cccccc] mb-2">
                    Instruções Adicionais
                  </label>
                  <textarea
                    value={config.prompt_sections.custom_instructions}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      prompt_sections: { ...prev.prompt_sections, custom_instructions: e.target.value }
                    }))}
                    disabled={!isEditing}
                    placeholder="Quaisquer instruções personalizadas adicionais..."
                    rows={4}
                    className={`${inputBaseClass} resize-none`}
                  />
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-[#cccccc] mb-2">
                  Prompt de Sistema Personalizado
                </label>
                <textarea
                  value={config.custom_prompt}
                  onChange={(e) => setConfig(prev => ({ ...prev, custom_prompt: e.target.value }))}
                  disabled={!isEditing}
                  placeholder="Digite seu prompt de sistema personalizado..."
                  rows={12}
                  className={`${inputBaseClass} resize-none`}
                />
              </div>
            )}
          </div>

          {/* Actions */}
          {isEditing && (
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#333333]">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                disabled={isLoading}
                className="
                  px-4 py-2
                  bg-[#333333] hover:bg-[#444444]
                  text-white text-sm font-medium
                  rounded-lg transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="
                  flex items-center justify-center gap-2 px-4 py-2 min-w-[140px]
                  bg-[#F4CC04] hover:bg-[#D4B104]
                  text-black text-sm font-medium
                  rounded-lg transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                {isLoading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar Alterações'
                )}
              </button>
            </div>
          )}
        </form>
      </AdminCard>

      {/* Tag Management Card */}
      <AdminCard title="Gerenciamento de Tags">
        {tagError && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm mb-6">
            {tagError}
          </div>
        )}

        <div className="space-y-6">
          {/* Create New Tag */}
          <div className="space-y-3">
            <h3 className="text-white font-medium text-sm">Criar Nova Tag</h3>
            <div className="flex gap-3">
              <input
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Nome da tag"
                maxLength={50}
                className={`${inputBaseClass} flex-1`}
              />
              <button
                type="button"
                onClick={handleCreateTag}
                disabled={isTagLoading || !newTagName.trim()}
                className="
                  flex items-center gap-2 px-4 py-2
                  bg-[#F4CC04] hover:bg-[#D4B104]
                  text-black text-sm font-medium
                  rounded-lg transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                  whitespace-nowrap
                "
              >
                <Plus className="w-4 h-4" />
                {isTagLoading ? 'Criando...' : 'Criar'}
              </button>
            </div>
          </div>

          {/* Agent Tags */}
          <div className="space-y-3">
            <h3 className="text-white font-medium text-sm">Tags do Agente</h3>
            {agentTags.length === 0 ? (
              <p className="text-[#888888] text-sm">Nenhuma tag associada a este agente</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {agentTags.map(tag => (
                  <div
                    key={tag.id}
                    className="
                      flex items-center gap-2 px-3 py-1.5
                      bg-[#F4CC04]/20 text-[#F4CC04]
                      rounded-full text-sm
                    "
                  >
                    <span>{tag.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTagFromAgent(tag.id)}
                      disabled={isTagLoading}
                      className="
                        p-0.5 hover:bg-[#F4CC04]/30 rounded-full
                        transition-colors disabled:opacity-50
                      "
                      title="Remover tag do agente"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Available Tags */}
          <div className="space-y-3">
            <h3 className="text-white font-medium text-sm">Tags Disponíveis</h3>
            {isTagLoading ? (
              <div className="flex items-center justify-center py-4">
                <LoadingSpinner size="small" />
              </div>
            ) : allTags.length === 0 ? (
              <p className="text-[#888888] text-sm">Nenhuma tag disponível</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {allTags.map(tag => {
                  const isAssociated = agentTags.some(agentTag => agentTag.id === tag.id);
                  return (
                    <div
                      key={tag.id}
                      className={`
                        flex items-center gap-2 px-3 py-1.5
                        rounded-full text-sm
                        ${isAssociated
                          ? 'bg-[#333333] text-[#888888]'
                          : 'bg-[#333333] text-[#cccccc]'
                        }
                      `}
                    >
                      <span>{tag.name}</span>
                      <div className="flex items-center gap-1">
                        {!isAssociated && (
                          <button
                            type="button"
                            onClick={() => handleAddTagToAgent(tag.id)}
                            disabled={isTagLoading}
                            className="
                              p-0.5 hover:bg-green-500/20 text-green-400
                              rounded-full transition-colors disabled:opacity-50
                            "
                            title="Adicionar tag ao agente"
                          >
                            <Check className="w-3 h-3" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDeleteTag(tag.id)}
                          disabled={isTagLoading}
                          className="
                            p-0.5 hover:bg-red-500/20 text-red-400
                            rounded-full transition-colors disabled:opacity-50
                          "
                          title="Excluir tag"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </AdminCard>
    </div>
  );
};

export default AgentConfiguration;
